import React, { useEffect, useState } from "react";
import { Tree, TreeNode } from "react-organizational-chart";
import { Modal, Input, Button } from "antd";
import "./ManagementOrgChart.css";
import Swal from "sweetalert2";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchorgchartapi } from "../api/fetchorgcahrtapi";
import { fetchorgchartupdateapi } from "../api/fetchorgcharupdateapi";
import { fetchorgchartaddapi } from "../api/fetchorgchartaddapi";
import { fetchorgchartdeleteapi } from "../api/fetchorchartdeleteapi";

const ManagementOrgChart = () => {
    const { t, i18n } = useTranslation();
    const [data, setData] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [mode, setMode] = useState("add");

    const navigate = useNavigate();
    const location = useLocation();

    const userDetails = JSON.parse(sessionStorage.getItem("userdetails"));

    const buildTree = (data) => {
        const map = {};
        const roots = [];

        data.forEach(item => {
            map[item.id] = { ...item, children: [] };
        });

        data.forEach(item => {
            if (item.parentId === 0) {
                roots.push(map[item.id]);
            } else {
                map[item.parentId]?.children.push(map[item.id]);
            }
        });

        return roots;
    };

    useEffect(() => {
        fetchOrgChart();
    }, []);

    const fetchOrgChart = async () => {
        try {
            const data = await fetchorgchartapi("managementOrgchart");
            setData(data);
        } catch (err) {
            console.error("Fetch failed:", err);
        }
    };

    const handleModalChange = (field, value) => {
        setEditId(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleAdd = (parentId) => {
        setEditId({ parentId });
        setMode("add");
        setIsModalOpen(true);
    };

    const handleSave = async () => {
        try {
            if (mode === "edit") {
                await fetchorgchartupdateapi(editId);
            } else {
                await fetchorgchartaddapi(editId, "managementOrgchart");
            }

            await fetchOrgChart();
            setIsModalOpen(false);
        } catch (err) {
            console.error("Save failed:", err);
        }
    };

    const handleDelete = (node) => {
        const Isnode = data.find(item => item.id === node?.id);

        if (!Isnode) return;

        Swal.fire({
            title: t("chooseAction"),
            html: `
      <div style="text-align:left">
        <p><b>${t("whatDoYouWantToDo")}</b></p>
        <ul style="padding-left:18px">
          <li><b>${t("deleteAll")}</b> → ${t("removeEmployeeAndSubordinates")}</li>
          <li><b>${t("reassign")}</b> → ${t("removeEmployeeMoveSubordinates")}</li>
        </ul>
      </div>
    `,
            icon: "warning",
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: t("deleteAll"),
            denyButtonText: t("reassign"),
            cancelButtonText: t("cancel"),
            confirmButtonColor: "#dc3545",
            denyButtonColor: "#0d6efd"
        }).then(async (result) => {
            if (result.isConfirmed) {

                await fetchorgchartdeleteapi(node, "delete");
                await fetchOrgChart();
                Swal.fire({
                    icon: "success",
                    title: t("deletedSuccessfully"),
                    text: t("employeeAndSubordinatesRemoved"),
                    timer: 1500,
                    showConfirmButton: false
                });

            } else if (result.isDenied) {
                await fetchorgchartdeleteapi(node, "reassign");
                await fetchOrgChart();
                Swal.fire({
                    icon: "success",
                    title: t("reassigned"),
                    text: t("subordinatesMovedToUpperLevel"),
                    timer: 1500,
                    showConfirmButton: false
                });
            }
        });
    };

    const name = (node) => {
        return (i18n.language === "ta" ? node.name_ta : node.name_en) || "";
    };

    const Position = (node) => {
        return (i18n.language === "ta" ? node.position_ta : node.position_en) || "";
    };

    const renderTree = (node) => {

        return (
            <>
                <TreeNode
                    key={node.id}
                    label={
                        <div className="org-card">

                            <div className="org-name" title={name(node)} style={{ cursor: "pointer" }} >{name(node)}</div>
                            <div className="org-role">{Position(node)}</div>

                            {location.pathname === "/warehouse-Admin/management-org-chart" && userDetails?.role === "Admin" && (
                                <div className="org-actions">
                                    <Button
                                        type="primary"
                                        className="org-btn btn-edit border-primary bg-primary"
                                        onClick={() => {
                                            setEditId(node);
                                            setMode("edit");
                                            setIsModalOpen(true);
                                        }}
                                    >
                                        <i className="bi bi-pencil"></i>
                                    </Button>

                                    <Button
                                        type="primary"
                                        className="org-btn btn-add border-success bg-success"
                                        onClick={() => handleAdd(node.id)}
                                    >
                                        <i className="bi bi-plus"></i>
                                    </Button>

                                    {node.parentId !== 0 && (
                                        <Button
                                            type="primary"
                                            className="org-btn btn-delete border-danger bg-danger"
                                            onClick={() => handleDelete(node)}
                                        >
                                            <i className="bi bi-trash"></i>
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    }
                >
                    {node.children.map((child) => renderTree(child))}
                </TreeNode>
                <Modal
                    title={t("editUser")}
                    open={isModalOpen}
                    onCancel={() => setIsModalOpen(false)}
                    centered
                    onOk={handleSave}
                    okText={t("save")}
                    cancelText={t("cancel")}
                >
                    <label className="mb-1 text-muted">{t("nameEnglish")}</label>
                    <Input
                        className="mb-3"
                        placeholder={t("nameInEnglish")}
                        value={editId?.name_en || ""}
                        onChange={(e) =>
                            handleModalChange("name_en", e.target.value)
                        }
                    />

                    <label className="mb-1 text-muted">{t("nameTamil")}</label>
                    <Input
                        className="mb-3"
                        placeholder={t("nameInTamil")}
                        value={editId?.name_ta || ""}
                        onChange={(e) =>
                            handleModalChange("name_ta", e.target.value)
                        }
                    />

                    <label className="mb-1 text-muted">{t("positionEnglish")}</label>
                    <Input
                        className="mb-3"
                        placeholder={t("positionInEnglish")}
                        value={editId?.position_en || ""}
                        onChange={(e) =>
                            handleModalChange("position_en", e.target.value)
                        }
                    />

                    <label className="mb-1 text-muted">{t("positionTamil")}</label>
                    <Input
                        className="mb-3"
                        placeholder={t("positionInTamil")}
                        value={editId?.position_ta || ""}
                        onChange={(e) =>
                            handleModalChange("position_ta", e.target.value)
                        }
                    />

                </Modal>
            </>
        );
    };

    const treeData = buildTree(data);

    return (
        <div className="container my-4 text-center">
            <div className="d-flex justify-content-end align-items-center mb-4">
                {location.pathname === "/warehouse-Admin/management-org-chart" && userDetails?.role === "Admin" && (
                    <Button className="border-primary" type="default" onClick={() => navigate(-1)}>
                        <i className="bi bi-arrow-left"></i> {t("back")}
                    </Button>
                )}
            </div>

            <div className="d-inline-flex align-items-center gap-2">
                <span
                    className="bg-danger rounded-pill"
                    style={{ width: "5px", height: "32px" }}
                ></span>

                <h2 className="fw-bold text-dark my-3">
                    {t("managementOrgChart")}
                </h2>
            </div>

            <Tree>
                {treeData.map(root => renderTree(root))}
            </Tree>
        </div>
    );
};

export default ManagementOrgChart;